/**
 * Altara Trust Certificate — public verify page. No auth. Anyone (consumer,
 * compliance officer, regulator, auditor) can paste a certificate ID or
 * follow a QR-scanned URL and:
 *   1. See the full signed certificate.
 *   2. Independently confirm the Ed25519 signature (backend re-verifies with
 *      the published JWKS).
 *   3. Optionally upload the file they received and get MATCH / MISMATCH.
 *   4. See the version chain (all revisions on the same delivery journey).
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, Loader2, Upload, X, FileText, CheckCircle2,
  AlertTriangle, KeyRound, ExternalLink, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function TrustCertificateVerifyPage() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [chain, setChain] = useState(null);
  const [error, setError] = useState(null);
  const [busyVerify, setBusyVerify] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/trust/certificates/${certId}`)
      .then(r => setCert(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Certificate not found"));
    axios.get(`${API}/trust/certificates/${certId}/chain`)
      .then(r => setChain(r.data))
      .catch(() => {});
    // Kick off a signature-only verification immediately so the header badge
    // shows GREEN as soon as the page loads.
    axios.post(`${API}/trust/certificates/${certId}/verify`)
      .then(r => setVerifyResult(r.data))
      .catch(() => {});
  }, [certId]);

  const runFileVerify = async () => {
    if (!file) return;
    setBusyVerify(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/trust/certificates/${certId}/verify`, fd);
      setVerifyResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Verification failed");
    } finally {
      setBusyVerify(false);
    }
  };

  if (error) return <PublicShell><ErrorCard message={error} /></PublicShell>;
  if (!cert) return <PublicShell><LoadingCard /></PublicShell>;

  const c = cert.envelope?.certificate || {};
  const sig = cert.envelope?.signature || {};
  const doc = c.document || {};
  const delivery = c.delivery || {};
  const depositor = c.depositor || {};
  const version = c.version || {};
  const sigOK = verifyResult?.signature_valid;
  const fileMatch = verifyResult?.file_match;

  return (
    <PublicShell>
      <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="verify-page">
        {/* Header */}
        <div className="px-6 lg:px-10 py-7 text-white" style={{ background: NAVY }}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Altara Trust Certificate
          </div>
          <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
            <h1 className="font-display text-2xl lg:text-3xl font-semibold break-all" data-testid="verify-cert-id">
              {c.certificate_id}
            </h1>
            <SignatureBadge sigOK={sigOK} />
          </div>
          <p className="mt-3 text-sm text-white/80 max-w-2xl">
            This certificate proves the origin, integrity, delivery time and version of a document deposited into an Altara Consumer Trust Wallet.
          </p>
        </div>

        {/* Body */}
        <div className="p-6 lg:p-10 space-y-8">
          {/* Fact grid */}
          <Section title="Delivery">
            <FactGrid>
              <Fact label="Source" value={humanSource(delivery.source)} />
              <Fact label="Delivered at" value={fmtDate(delivery.delivered_at)} />
              <Fact label="Issued at" value={fmtDate(c.issued_at)} />
              <Fact label="Consumer" value={delivery.consumer_display_name} />
              <Fact label="Journey ID" value={delivery.journey_id} mono />
              {delivery.request_id && <Fact label="Consumer request ID" value={delivery.request_id} mono />}
            </FactGrid>
          </Section>

          <Section title="Depositor">
            <FactGrid>
              <Fact label="Name (claimed)" value={depositor.name || "—"} />
              <Fact label="Email (claimed)" value={depositor.email || "—"} />
              <Fact label="Institution (claim)" value={depositor.institution || "—"} />
              <Fact label="Identity verified" value={depositor.identity_verified ? "Yes" : "No — self-declared"} />
            </FactGrid>
            {!depositor.identity_verified && (
              <div className="mt-3 flex items-start gap-2 rounded-lg p-3 text-[12px]" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="depositor-disclaimer">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{depositor.verification_note}</span>
              </div>
            )}
          </Section>

          <Section title="Document integrity">
            <FactGrid>
              <Fact label="Filename" value={doc.filename} />
              <Fact label="MIME type" value={doc.mime_type} />
              <Fact label="Size (bytes)" value={String(doc.size_bytes ?? "")} />
              <Fact label="Document ID" value={doc.document_id} mono />
            </FactGrid>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">File SHA-256 (expected)</div>
              <div className="mt-1 font-mono text-[11px] text-slate-700 break-all bg-slate-50 rounded-lg p-3">{doc.sha256}</div>
            </div>
          </Section>

          <Section title="Version">
            <FactGrid>
              <Fact label="Upload count" value={String(version.upload_count ?? 1)} />
              <Fact label="Is latest" value={version.is_latest ? "Yes" : "No — a later revision exists"} />
              <Fact label="Prev cert hash" value={version.prev_hash || "—"} mono />
            </FactGrid>
            {version.supersedes?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Supersedes</div>
                <div className="flex flex-wrap gap-1.5">
                  {version.supersedes.map(id => (
                    <a key={id} href={`/verify/${id}`} className="font-mono text-[11px] rounded-lg px-2.5 py-1 border" style={{ color: NAVY, borderColor: `${NAVY}22`, background: "white" }}>{id}</a>
                  ))}
                </div>
              </div>
            )}
            {chain?.chain?.length > 1 && (
              <div className="mt-4 rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-white" style={{ background: NAVY }}>
                  Delivery chain · {chain.chain.length} revisions on this journey
                </div>
                <div>
                  {chain.chain.map(row => (
                    <div key={row.certificate_id} className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-[12px]">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-slate-400">#{row.upload_count}</span>
                        <a href={`/verify/${row.certificate_id}`} className="font-mono truncate hover:underline" style={{ color: NAVY }}>{row.certificate_id}</a>
                        {row.is_latest && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: `${GOLD}30`, color: NAVY }}>Latest</span>}
                      </div>
                      <span className="font-mono text-slate-500 text-[10px]">{fmtDate(row.issued_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* File verification widget */}
          <Section title="Compare the file you received">
            <p className="text-[13px] text-slate-600">
              Drop the file you actually received here. Altara will re-hash it and confirm whether it matches the file recorded in this certificate. Your file is <strong>never uploaded</strong> to permanent storage — it is hashed and discarded.
            </p>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              onClick={() => fileRef.current?.click()}
              data-testid="verify-dropzone"
              className={`mt-3 rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition ${dragOver ? "border-slate-400 bg-slate-50" : file ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
            >
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium" style={{ color: NAVY }}>{file.name}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); setVerifyResult(v => v ? { ...v, file_match: null, supplied_sha256: null } : v); }} className="text-slate-400 hover:text-rose-600 ml-2"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6 mx-auto text-slate-400" />
                  <div className="mt-2 text-sm text-slate-600">Drop file here or <span className="underline" style={{ color: NAVY }}>choose a file</span></div>
                </>
              )}
              <input ref={fileRef} type="file" data-testid="verify-file" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>

            {file && (
              <button
                onClick={runFileVerify}
                disabled={busyVerify}
                data-testid="verify-run"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-40"
                style={{ background: NAVY, color: "white" }}
              >
                {busyVerify ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : <><ShieldCheck className="w-4 h-4" /> Verify this file against the certificate</>}
              </button>
            )}

            {verifyResult && verifyResult.supplied_sha256 && (
              <div
                className={`mt-4 rounded-2xl border p-4 ${fileMatch ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}
                data-testid="verify-file-result"
              >
                <div className="flex items-center gap-2 font-semibold" style={{ color: fileMatch ? "#065F46" : "#9F1239" }}>
                  {fileMatch ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                  {fileMatch ? "File matches — integrity confirmed" : "File does NOT match this certificate"}
                </div>
                <div className="mt-3 space-y-1 text-[11px] font-mono">
                  <div><span className="text-slate-500">Expected · </span>{verifyResult.expected_sha256}</div>
                  <div><span className="text-slate-500">Supplied · </span>{verifyResult.supplied_sha256}</div>
                </div>
              </div>
            )}
          </Section>

          {/* Signature panel */}
          <Section title="Cryptographic proof">
            <FactGrid>
              <Fact label="Algorithm" value={sig.alg || "Ed25519"} />
              <Fact label="Signing key ID" value={sig.kid} mono />
              <Fact label="Payload SHA-256" value={sig.payload_sha256} mono />
            </FactGrid>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Ed25519 Signature</div>
              <div className="mt-1 font-mono text-[11px] text-slate-700 break-all bg-slate-50 rounded-lg p-3">{sig.value}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={`${API}/trust/certificates/${certId}/pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="download-pdf"
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-medium border"
                style={{ background: NAVY, color: "white", borderColor: NAVY }}
              >
                <FileText className="w-3.5 h-3.5" /> Download PDF
              </a>
              <a
                href={`${API}/trust/certificates/${certId}`}
                target="_blank"
                rel="noreferrer"
                data-testid="download-json"
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-medium border bg-white hover:bg-slate-50"
                style={{ color: NAVY, borderColor: `${NAVY}22` }}
              >
                <ExternalLink className="w-3.5 h-3.5" /> View signed JSON
              </a>
              <a
                href={`${API}/trust/public-key`}
                target="_blank"
                rel="noreferrer"
                data-testid="view-jwks"
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-medium border bg-white hover:bg-slate-50"
                style={{ color: NAVY, borderColor: `${NAVY}22` }}
              >
                <KeyRound className="w-3.5 h-3.5" /> Public key (JWKS)
              </a>
            </div>
          </Section>
        </div>
      </div>
    </PublicShell>
  );
}

function SignatureBadge({ sigOK }) {
  if (sigOK === undefined) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium" style={{ background: "rgba(255,255,255,0.15)", color: "white" }}>
        <Loader2 className="w-3 h-3 animate-spin" /> Verifying signature…
      </span>
    );
  }
  if (sigOK) {
    return (
      <span data-testid="sig-badge-ok" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold" style={{ background: GOLD, color: NAVY }}>
        <ShieldCheck className="w-3.5 h-3.5" /> Signature valid
      </span>
    );
  }
  return (
    <span data-testid="sig-badge-bad" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold" style={{ background: "#B91C1C", color: "white" }}>
      <ShieldAlert className="w-3.5 h-3.5" /> Signature invalid
    </span>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: NAVY }}>{title}</h2>
      {children}
    </section>
  );
}

function FactGrid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>;
}

function Fact({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className={`text-[13px] break-all ${mono ? "font-mono text-slate-700" : ""}`} style={{ color: mono ? undefined : NAVY }}>{value || "—"}</div>
    </div>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen py-8 lg:py-14 px-4" style={{ background: "#F8F9FA" }}>
      <div className="max-w-3xl mx-auto">
        <a href="/" className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-display font-bold text-white" style={{ background: NAVY }}>A</div>
          <div className="leading-tight">
            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Altara</div>
            <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Trust Certificate</div>
          </div>
        </a>
        {children}
        <div className="mt-6 text-[10px] text-slate-400 text-center">
          Altara Consumer Trust Infrastructure · <a href="/" className="underline">altara.co.za</a>
        </div>
      </div>
    </div>
  );
}

function LoadingCard() {
  return (
    <div className="rounded-3xl bg-white shadow-xl border border-slate-100 p-10 text-center" data-testid="verify-loading">
      <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: NAVY }} />
      <p className="mt-3 text-sm text-slate-600">Loading certificate…</p>
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div className="rounded-3xl bg-white shadow-xl border border-rose-200 overflow-hidden" data-testid="verify-error">
      <div className="px-6 lg:px-10 py-8 flex items-center gap-4 bg-rose-50">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-rose-600">
          <ShieldAlert className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-rose-800">Not found</div>
          <h1 className="font-display text-xl font-semibold text-rose-900 mt-1">{message}</h1>
        </div>
      </div>
      <div className="p-6 lg:p-10">
        <p className="text-sm text-slate-700">Double-check the certificate ID or ask the person who shared this link to re-issue it.</p>
      </div>
    </div>
  );
}

function humanSource(s) {
  if (s === "consumer_led_dropoff") return "Consumer-led drop-off link";
  if (s === "banker_push") return "Institution push (banker-initiated)";
  if (s === "backfill") return "Retro-issued (backfill)";
  return s || "—";
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
